/**
 * 需要想定 (週間) 画面の page 内の型と純関数。
 *
 * wire 型 (bffuser GetDemandForecastWeeklyResponse) は `parseWeeklyPage` でここの型へ
 * 写し、画面は以後 wire 型を読まない。選択日の 48 コマ表・チャートは単日の部品を
 * そのまま使うため `buildSelectedDayView` で `ForecastSectionView` を組む。
 * 単日の builder を通らないので、builder が保証していた invariant (当日以外の
 * currentKoma = 0、読み取り専用、参照列なし) はここで固定する。
 */
import type {
  DayWeatherSummary,
  GetDemandForecastWeeklyResponse,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'

import { KOMA_COUNT } from '~/constants/koma'
import type { CivilDate } from '~/models/civilDate'
import { civilDateEquals, protoDateToCivilDate } from '~/models/civilDate'
import type { GateCloseFrontier } from '~/models/gateClose'
import {
  gateCloseFrontierFromProto,
  isKomaGateClosed,
} from '~/models/gateClose'
import type { ForecastSectionView } from '~/pages/DemandForecastDaily/models/forecastSectionView'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

export type WeeklyDayWeather =
  | {
      kind: 'forecast'
      weatherCode: number | undefined
      maxTemperature: number | undefined
      minTemperature: number | undefined
      normalDeviation: number | undefined
    }
  | { kind: 'beyondHorizon' }
  | { kind: 'unavailable' }

export type WeeklyDay = {
  date: CivilDate
  slots: KomaSlots
  maxPowerMw: number | undefined
  minPowerMw: number | undefined
  energyMwh: number | undefined
  weather: WeeklyDayWeather
}

export type WeeklyPage = {
  days: WeeklyDay[]
  observationPointName: string
  gateCloseFrontier: GateCloseFrontier | undefined
  /** 公開予報の範囲外 (AC-WD-005d) の日が 1 日でもあるか。 */
  hasBeyondHorizon: boolean
}

function decimalToNumber(d: Decimal | undefined): number | undefined {
  if (d === undefined || d.value === '') return undefined
  const n = Number(d.value)
  return Number.isFinite(n) ? n : undefined
}

function parseWeather(w: DayWeatherSummary | undefined): WeeklyDayWeather {
  const state = w?.state
  switch (state?.case) {
    case 'forecast':
      return {
        kind: 'forecast',
        weatherCode: state.value.dailyWeatherCode,
        maxTemperature: decimalToNumber(state.value.maxTemperature),
        minTemperature: decimalToNumber(state.value.minTemperature),
        normalDeviation: decimalToNumber(state.value.normalDeviation),
      }
    case 'beyondHorizon':
      return { kind: 'beyondHorizon' }
    default:
      return { kind: 'unavailable' }
  }
}

/** wire 型を page 内の型にする。日付の読めない日は落とす。 */
export function parseWeeklyPage(
  res: GetDemandForecastWeeklyResponse,
): WeeklyPage {
  const days: WeeklyDay[] = []
  for (const d of res.page?.days ?? []) {
    const date = protoDateToCivilDate(d.date)
    if (date === undefined) continue
    const slots: KomaSlots = Array.from({ length: KOMA_COUNT }, (_, i) =>
      decimalToNumber(d.bgTotalSlots[i]?.value),
    )
    days.push({
      date,
      slots,
      maxPowerMw: decimalToNumber(d.power?.maxPowerMw),
      minPowerMw: decimalToNumber(d.power?.minPowerMw),
      energyMwh: decimalToNumber(d.power?.energyMwh),
      weather: parseWeather(d.weather),
    })
  }
  return {
    days,
    observationPointName: res.page?.observationPointName ?? '',
    gateCloseFrontier: gateCloseFrontierFromProto(res.page?.gateCloseFrontier),
    hasBeyondHorizon: days.some((d) => d.weather.kind === 'beyondHorizon'),
  }
}

/** 選択日。未指定・期間外は先頭 (= ケースの対象日)。days が空の時だけ undefined。 */
export function selectDay(
  page: WeeklyPage,
  picked: CivilDate | undefined,
): WeeklyDay | undefined {
  if (picked !== undefined) {
    const found = page.days.find((d) => civilDateEquals(d.date, picked))
    if (found !== undefined) return found
  }
  return page.days[0]
}

/**
 * 選択日を単日部品の `ForecastSectionView` にする。読み取り専用で、当日実績・
 * 参照列・前日/翌日の延長を渡さない。差の列の基準は表示値 (= 差は常に 0)。
 * GC 済みのコマ数は page の frontier と選択日から数える (今日判定をしない)。
 */
export function buildSelectedDayView(
  day: WeeklyDay,
  frontier: GateCloseFrontier | undefined,
): ForecastSectionView {
  let gcKoma = 0
  for (let koma = 1; koma <= KOMA_COUNT; koma++) {
    if (isKomaGateClosed(frontier, day.date, koma)) gcKoma = koma
  }
  return {
    date: { isReferenceView: true },
    series: {
      forecast: day.slots,
      pastActuals: [],
      confirmRecordRefs: [],
      referenceColumns: [],
    },
    presence: {
      hasTodayActual: false,
      hasTrendForecast: false,
      showTemperature: false,
      hasTempForecast: false,
      hasTempActual: false,
      hasDewpointData: false,
    },
    cells: {
      gcKoma,
      currentKoma: 0,
      gcLine:
        frontier === undefined ? undefined : { frontier, targetDate: day.date },
      manualEditKomas: new Set(),
      dirtyKomas: new Set(),
    },
    editing: {
      isEditable: false,
      hasEditRow: false,
      savedBaseline: day.slots,
      isReadOnly: true,
      isAggregateScope: false,
    },
    targetLabel: '',
    source: { providerLabel: undefined, isDraftMissing: false },
    status: { pastActualsFailed: false },
  }
}

const fmt = (v: number | undefined, digits: number): string =>
  v === undefined ? '' : v.toFixed(digits)

/** サマリ表の 1 列の表示値 (AC-DFW-001)。範囲外・取得なしの日の気象は空。 */
export type WeeklySummaryColumn = {
  date: CivilDate
  weatherCode: number | undefined
  maxTemperature: string
  minTemperature: string
  normalDeviation: string
  maxPower: string
  minPower: string
  energy: string
}

export function summaryColumnOf(day: WeeklyDay): WeeklySummaryColumn {
  const w = day.weather.kind === 'forecast' ? day.weather : undefined
  const dev = w?.normalDeviation
  return {
    date: day.date,
    weatherCode: w?.weatherCode,
    maxTemperature: fmt(w?.maxTemperature, 1),
    minTemperature: fmt(w?.minTemperature, 1),
    normalDeviation:
      dev === undefined ? '' : `${dev > 0 ? '+' : ''}${dev.toFixed(1)}`,
    maxPower: fmt(day.maxPowerMw, 2),
    minPower: fmt(day.minPowerMw, 2),
    energy: fmt(day.energyMwh, 0),
  }
}
